import {
  Modal,
  Text,
  Group,
  Button,
  Stack,
  Box,
  ScrollArea,
} from "@mantine/core";
import { IconReplace, IconTextPlus, IconX } from "@tabler/icons-react";
import { stripHtml } from "../../utils/grossTemplate";

interface TemplateChangeDialogProps {
  opened: boolean;
  onClose: () => void;
  onReplace: () => void;
  onAppend: () => void;
  templatePreview: string;
  hasManualText?: boolean; // If true, only show Append option (no Replace)
}

export default function TemplateChangeDialog({
  opened,
  onClose,
  onReplace,
  onAppend,
  templatePreview,
  hasManualText = false,
}: TemplateChangeDialogProps) {
  const plainPreview = stripHtml(templatePreview);
  const maxPreviewLength = 200;
  const truncatedPreview =
    plainPreview.length > maxPreviewLength
      ? `${plainPreview.substring(0, maxPreviewLength)}...`
      : plainPreview;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Change Template?"
      centered
      size="lg"
    >
      <Stack gap="md">
        <Text size="sm">
          {hasManualText
            ? "You have manual changes. Choose how to apply the new template:"
            : "You have existing text in the gross description. How would you like to apply the new template?"}
        </Text>

        {hasManualText && (
          <Text size="xs" c="orange" fw={500}>
            ⚠️ Warning: "Replace All" will permanently delete your manual
            changes.
          </Text>
        )}

        {/* Template Preview */}
        <Stack gap="xs">
          <Text size="xs" c="dimmed" fw={500}>
            New template preview:
          </Text>
          <ScrollArea.Autosize mah={120}>
            <Box
              p="xs"
              style={{
                backgroundColor: "var(--mantine-color-gray-0)",
                borderRadius: "var(--mantine-radius-sm)",
                border: "1px solid var(--mantine-color-gray-3)",
                fontSize: "12px",
                whiteSpace: "pre-wrap",
              }}
            >
              <Text size="xs">{truncatedPreview}</Text>
            </Box>
          </ScrollArea.Autosize>
        </Stack>

        {/* Action Buttons */}
        <Group justify="flex-end" gap="sm" mt="md">
          <Button
            variant="subtle"
            color="gray"
            leftSection={<IconX size={16} />}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="light"
            color="blue"
            leftSection={<IconTextPlus size={16} />}
            onClick={onAppend}
          >
            Append to Existing
          </Button>
          <Button
            color={hasManualText ? "red" : "blue"}
            leftSection={<IconReplace size={16} />}
            onClick={onReplace}
          >
            Replace All
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
